import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormHelperText,
  DialogContentText,
  CircularProgress,
  Autocomplete,
  TextField,
  Checkbox,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  Grid
} from '@mui/material';
import { 
  Check as CheckIcon, 
  Edit as EditIcon, 
  Visibility as ViewIcon, 
  AdminPanelSettings as AdminIcon 
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { companiesSelector } from 'app/selectors/companiesSelector';
import { addInfo, addError } from 'app/actions/snackbar/snackbar';
import { usePersonaSharePersonaWithCompanies, usePersonaGetPersonaCompanies } from '@/api-gen/api';
import type { SimpleCompanyDto, AccessLevel } from '@/api-gen/api.schemas';

import type { I18nPersonasNs, I18nCommonNs } from '@/i18n/dictionaries/interfaces';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import { useTranslation } from 'react-i18next';

interface ShareWithCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  personaId?: number;
}

interface CompanyWithAccess {
  company: SimpleCompanyDto;
  access: AccessLevel;
}

const ShareWithCompanyDialog: React.FC<ShareWithCompanyDialogProps> = ({
  open,
  onClose,
  personaId
}) => {
  const dispatch = useDispatch();
  const allCompanies = useSelector(companiesSelector);
  const [selectedCompaniesWithAccess, setSelectedCompaniesWithAccess] = useState<CompanyWithAccess[]>([]);
  
  const { t: translate } = useTranslation(I18nNamespace.Personas);
  const { t: translateCommon } = useTranslation(I18nNamespace.Common);

  // Get already shared companies
  const { data: existingSharesResponse } = usePersonaGetPersonaCompanies(personaId!, {
    query: {
      enabled: !!personaId && open
    }
  });

  const existingShares = Array.isArray(existingSharesResponse)
    ? existingSharesResponse
    : (existingSharesResponse?.data ?? []);

  // Filter out already shared companies
  const availableCompanies = React.useMemo(() => {
    if (!existingShares) return allCompanies;
    const sharedCompanyIds = new Set(existingShares.map(share => share.companyId));
    return allCompanies.filter(company => !sharedCompanyIds.has(company.id));
  }, [allCompanies, existingShares]);

  const shareMutation = usePersonaSharePersonaWithCompanies({
    mutation: {
      onSuccess: () => {
        dispatch(addInfo({ infoText: translate(nameof.full<I18nPersonasNs>((n) => n.messages.personaSharedSuccess)) }));
        handleClose();
      },
      onError: (error: any) => {
        dispatch(addError({ errorText: error?.message || translate(nameof.full<I18nPersonasNs>((n) => n.messages.sharePersonaError)) }));
      }
    }
  });

  const handleCompaniesChange = (_: any, newCompanies: SimpleCompanyDto[]) => {
    const existingMap = new Map(
      selectedCompaniesWithAccess.map(item => [item.company.id, item.access])
    );

    const newSelection = newCompanies.map(company => ({
      company,
      access: existingMap.get(company.id) ?? 'CanRead' as AccessLevel
    }));

    setSelectedCompaniesWithAccess(newSelection);
  };

  const handleAccessChange = (companyId: number | undefined, access: AccessLevel) => {
    if (!companyId) return;

    setSelectedCompaniesWithAccess(prev => 
      prev.map(item => 
        item.company.id === companyId 
          ? { ...item, access } 
          : item
      )
    );
  };

  const handleSubmit = () => {
    if (selectedCompaniesWithAccess.length === 0 || !personaId) return;
    
    shareMutation.mutate({
      id: personaId,
      data: selectedCompaniesWithAccess.map(({ company, access }) => ({
        companyId: company.id!,
        access
      }))
    });
  };

  const handleClose = () => {
    setSelectedCompaniesWithAccess([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{translate(nameof.full<I18nPersonasNs>((n) => n.sharing.shareWithCompanies))}</DialogTitle>
      <DialogContent>
        <DialogContentText gutterBottom>
          {translate(nameof.full<I18nPersonasNs>((n) => n.sharing.selectCompaniesHelper))}
        </DialogContentText>

        <FormControl fullWidth sx={{ mt: 2 }}>
          <Autocomplete
            multiple
            id="shared-companies-select"
            options={availableCompanies}
            getOptionLabel={(option) => option.name || ''}
            value={selectedCompaniesWithAccess.map(item => item.company)}
            onChange={handleCompaniesChange}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckIcon style={{ visibility: 'hidden' }} />}
                  checkedIcon={<CheckIcon />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                <Typography variant="body2">{option.name}</Typography>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate(nameof.full<I18nPersonasNs>((n) => n.sharing.selectCompanies))}
                placeholder={translate(nameof.full<I18nPersonasNs>((n) => n.sharing.selectCompaniesPlaceholder))}
              />
            )}
          />
          <FormHelperText>
            {selectedCompaniesWithAccess.length === 0
              ? translate(nameof.full<I18nPersonasNs>((n) => n.sharing.selectCompaniesHelper))
              : `${selectedCompaniesWithAccess.length} ${translate(nameof.full<I18nPersonasNs>((n) => n.sharing.companiesSelectedHelper))}`}
          </FormHelperText>
        </FormControl>

        {selectedCompaniesWithAccess.length > 0 && (
          <Box mt={3}>
            <Typography variant="subtitle2" gutterBottom>
              {translate(nameof.full<I18nPersonasNs>((n) => n.sharing.accessLevels))}
            </Typography>
            <Stack spacing={2} mt={1}>
              {selectedCompaniesWithAccess.map(({ company, access }) => (
                <Box 
                  key={company.id} 
                  sx={{ 
                    p: 2, 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 1,
                    bgcolor: 'background.paper' 
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body1" fontWeight={500}>
                        {company.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <RadioGroup
                        row
                        value={access}
                        onChange={(e) => handleAccessChange(
                          company.id,
                          e.target.value as AccessLevel
                        )}
                      >
                        <FormControlLabel
                          value="CanRead"
                          control={<Radio size="small" />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <ViewIcon fontSize="small" sx={{ mr: 0.5 }} />
                              <Typography variant="body2">{translate(nameof.full<I18nPersonasNs>((n) => n.accessLevels.canRead))}</Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="CanEdit"
                          control={<Radio size="small" />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <EditIcon fontSize="small" sx={{ mr: 0.5 }} />
                              <Typography variant="body2">{translate(nameof.full<I18nPersonasNs>((n) => n.accessLevels.canEdit))}</Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="CanAdministrate"
                          control={<Radio size="small" />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <AdminIcon fontSize="small" sx={{ mr: 0.5 }} />
                              <Typography variant="body2">{translate(nameof.full<I18nPersonasNs>((n) => n.accessLevels.canAdministrate))}</Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={shareMutation.isLoading}>
          {translateCommon(nameof.full<I18nCommonNs>((n) => n.buttonLabels.cancel))}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={shareMutation.isLoading || selectedCompaniesWithAccess.length === 0}
          startIcon={shareMutation.isLoading ? <CircularProgress size={20} /> : null}
        >
          {translate(nameof.full<I18nPersonasNs>((n) => n.sharing.sharePersona))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareWithCompanyDialog;
